/**
 * Накладка неотправленных действий на карточку заказа.
 *
 * Без сети действие уходит в outbox, но карточка заказа читала только ответ сервера:
 * замерщик сохранял изделие, не видел его в комнате и вводил ещё раз (дубль после
 * синхронизации). Теперь при КАЖДОМ чтении заказа поверх последнего ответа сервера
 * проигрываем ожидающие записи из outbox этого заказа (по порядку). После отправки
 * запись удаляется из outbox, сервер уже содержит изменение — накладка исчезает сама.
 *
 * Чистая функция `applyPendingOps` не знает про IndexedDB — её легко тестировать.
 * Изделия из очереди получают id `pending:<outboxId>:<n>` и флаг pendingSync: править
 * и удалять их до синхронизации нельзя (сервер не знает этот id).
 */
import type { OrderDetail } from '@jaluzi/types';
import { applyMarkupAndDiscount, pbLamellaSnapshot, roundOrderTotal, type PbLamellaLayout } from '@jaluzi/calc/pricing';
import type { PbOffer } from '../features/pricebook/queries.js';
import { db, type OutboxItem } from './db/schema.js';
import { belongsToCurrentUser } from './queue-owner.js';
import { retainedSelectionOf, type OfflineRetainedSelection } from './offline-price.js';

type Room = OrderDetail['rooms'][number];
type Item = Room['items'][number];
export type OverlayItem = Item & { pendingSync?: boolean };
export type PendingOp = Pick<OutboxItem, 'id' | 'method' | 'url' | 'body' | 'createdAt'>;

export const PENDING_ID_PREFIX = 'pending:';
export const isPendingId = (id: string | null | undefined): boolean => !!id && id.startsWith(PENDING_ID_PREFIX);
export const isPendingItem = (item: { id: string; pendingSync?: boolean }): boolean => !!item.pendingSync || isPendingId(item.id);

/** Оценка цены строки изделия (× quantity) без сервера; null — цена появится после синхронизации. */
export type PriceEstimator = (item: ItemBody, size: { widthMm: number; heightMm: number; quantity: number }) => bigint | null;

interface ItemBody {
  type: string;
  widthMm: number;
  heightMm: number;
  quantity?: number;
  fabricId?: string | null;
  ribbonId?: string | null;
  mechanismId?: string | null;
  corniceId?: string | null;
  corniceColor?: string | null;
  slatSizeMm?: number | null;
  slatIncluded?: boolean | null;
  bortSizeMm?: number | null;
  bahroma?: boolean | null;
  notes?: string | null;
  priceBook?: { tableId: string; rowId: string; optionKey: string; withoutCornice?: boolean; extraRowIds?: string[]; lamellaLayout?: PbLamellaLayout | null } | null;
  mechanismRowId?: string | null;
  mechanismTypeId?: string | null;
  mechanismLabel?: string | null;
  /** Правка: сохранённый выбор изделия — для офлайн-оценки, как на сервере. */
  retained?: OfflineRetainedSelection | null;
}

interface MeasurementBody {
  item: ItemBody;
  count?: number;
  sizeRows?: Array<{ id?: string; widthMm: number; heightMm: number; quantity?: number }>;
}

/** Имена из прайса — чтобы изделие из очереди выглядело так же, как сохранённое сервером. */
function priceBookLabels(offer: PbOffer | undefined, pb: ItemBody['priceBook'], widthMm: number): Partial<Item> {
  if (!pb || !offer) return pb ? { pbTableId: pb.tableId, pbRowId: pb.rowId, pbOptionKey: pb.optionKey, pbWithoutCornice: !!pb.withoutCornice } : {};
  for (const section of offer.sections) {
    const table = section.tables.find((t) => t.id === pb.tableId);
    if (!table) continue;
    const row = table.rows.find((r) => r.id === pb.rowId);
    const parent = row?.parentRowId ? table.rows.find((r) => r.id === row.parentRowId) : null;
    const option = table.options.find((o) => o.key === pb.optionKey);
    // Ещё ткани изделия — тем же снимком, что сохранит сервер (pbExtraRows).
    const pbExtraRows = (pb.extraRowIds ?? []).flatMap((rowId) => {
      const extra = table.rows.find((r) => r.id === rowId);
      if (!extra) return [];
      const extraParent = extra.parentRowId ? table.rows.find((r) => r.id === extra.parentRowId) : null;
      return [{
        rowId,
        rowGroupLabel: (extraParent ? extraParent.label : extra.groupLabel) ?? null,
        rowLabel: extra.label,
        rowLabel2: extra.label2 ?? null,
        priceCents: 0,
      }];
    });
    return {
      pbSectionCode: section.code,
      pbTableId: table.id,
      pbRowId: pb.rowId,
      pbOptionKey: pb.optionKey,
      pbWithoutCornice: !!pb.withoutCornice,
      pbRowGroupLabel: (parent ? parent.label : row?.groupLabel) ?? null,
      pbRowLabel: row?.label ?? null,
      pbRowLabel2: row?.label2 ?? null,
      pbExtraRows,
      pbLamellaLayout: pb.lamellaLayout && pbExtraRows.length === 1
        ? pbLamellaSnapshot(pb.lamellaLayout, widthMm)
        : null,
      pbColumnLabel: option?.label ?? null,
    };
  }
  return { pbTableId: pb.tableId, pbRowId: pb.rowId, pbOptionKey: pb.optionKey, pbWithoutCornice: !!pb.withoutCornice };
}

function buildItem(
  id: string, measurementId: string, roomId: string, body: ItemBody,
  size: { widthMm: number; heightMm: number; quantity: number },
  offer: PbOffer | undefined, estimate: PriceEstimator | undefined,
): OverlayItem {
  return {
    id, measurementId, roomId,
    type: body.type,
    widthMm: size.widthMm,
    heightMm: size.heightMm,
    quantity: size.quantity,
    fabricId: body.priceBook ? null : body.fabricId ?? null,
    ribbonId: body.priceBook ? null : body.ribbonId ?? null,
    mechanismId: body.priceBook ? null : body.mechanismId ?? null,
    corniceId: body.priceBook ? null : body.corniceId ?? null,
    corniceColor: body.corniceColor?.trim() || null,
    slatSizeMm: body.slatSizeMm ?? null,
    slatIncluded: body.slatIncluded ?? null,
    bortSizeMm: body.bortSizeMm ?? null,
    bahroma: body.bahroma ?? null,
    photoUrl: null,
    notes: body.notes?.trim() || null,
    priceTiyin: (estimate?.(body, size) ?? 0n).toString(),
    costTiyin: '0',
    mechanismRowId: body.mechanismRowId ?? null,
    mechanismTypeId: body.mechanismTypeId ?? null,
    mechanismName: body.mechanismLabel ?? null,
    ...priceBookLabels(offer, body.priceBook, body.widthMm),
    pendingSync: true,
  };
}

/** Размеры, которые сервер создаст из тела замера (те же правила, что saveMeasurement). */
function sizesOf(body: MeasurementBody): Array<{ id?: string; widthMm: number; heightMm: number; quantity: number }> {
  if (body.sizeRows?.length) return body.sizeRows.map((r) => ({ id: r.id, widthMm: r.widthMm, heightMm: r.heightMm, quantity: r.quantity ?? 1 }));
  const count = Math.max(1, body.count ?? 1);
  return Array.from({ length: count }, () => ({ widthMm: body.item.widthMm, heightMm: body.item.heightMm, quantity: body.item.quantity ?? 1 }));
}

const ADDRESS_FIELDS = ['address', 'district', 'city', 'street', 'house', 'entrance', 'floor', 'apt'] as const;

/** Проиграть ожидающие записи поверх ответа сервера. Не мутирует исходный заказ. */
export function applyPendingOps(
  source: OrderDetail,
  ops: readonly PendingOp[],
  opts: { offer?: PbOffer; estimate?: PriceEstimator } = {},
): OrderDetail {
  if (!ops.length) return source;
  const order = structuredClone(source);
  // Поля адреса/расписания/наценки пишем как есть из тела запроса — их проверил сервер-DTO.
  const loose = order as unknown as Record<string, unknown>;
  const prefix = `/v1/orders/${source.id}/`;
  const roomById = (id: string) => order.rooms.find((r) => r.id === id);

  for (const op of ops) {
    if (!op.url.startsWith(prefix)) continue;
    const path = op.url.slice(prefix.length).split('?')[0]!;
    const body = (op.body ?? {}) as Record<string, unknown>;
    const opKey = `${PENDING_ID_PREFIX}${op.id ?? op.createdAt}`;
    let m: RegExpMatchArray | null;

    if (path === 'rooms' && op.method === 'POST') {
      const id = typeof body.id === 'string' ? body.id : opKey;
      if (!roomById(id)) {
        const position = order.rooms.reduce((max, r) => Math.max(max, r.position), 0) + 1;
        order.rooms.push({ id, name: String(body.name ?? ''), position, items: [] } as unknown as Room);
      }
    } else if ((m = path.match(/^rooms\/([^/]+)$/))) {
      const roomId = m[1]!;
      if (op.method === 'DELETE') order.rooms = order.rooms.filter((r) => r.id !== roomId);
      else if (op.method === 'PATCH' && typeof body.name === 'string') {
        const room = roomById(roomId);
        if (room) room.name = body.name;
      }
    } else if ((m = path.match(/^rooms\/([^/]+)\/measurements(?:\/([^/]+))?$/))) {
      const room = roomById(m[1]!);
      const measurementId = m[2];
      if (!room) continue;
      if (op.method === 'DELETE' && measurementId) {
        room.items = room.items.filter((i) => i.measurementId !== measurementId && i.id !== measurementId);
        continue;
      }
      const mb = body as unknown as MeasurementBody;
      if (!mb.item) continue;
      const sizes = sizesOf(mb);
      if (op.method === 'POST') {
        sizes.forEach((size, n) => room.items.push(buildItem(`${opKey}:${n}`, opKey, room.id, mb.item, size, opts.offer, opts.estimate)));
      } else if (op.method === 'PATCH' && measurementId) {
        const old = room.items.filter((i) => i.measurementId === measurementId || i.id === measurementId);
        const at = room.items.findIndex((i) => old.includes(i));
        const replaced = sizes.map((size, n) => ({
          ...buildItem(size.id ?? old[n]?.id ?? `${opKey}:${n}`, measurementId, room.id,
            { ...mb.item, retained: retainedSelectionOf(old[n] ?? old[0]) }, size, opts.offer, opts.estimate),
          // Правка существующей строки: id настоящий, ревизия та же — до синхронизации считаем «в пути».
          measurementRevision: old[0]?.measurementRevision,
        }));
        room.items = room.items.filter((i) => !old.includes(i));
        room.items.splice(at < 0 ? room.items.length : at, 0, ...replaced);
      }
    } else if ((m = path.match(/^rooms\/([^/]+)\/items\/([^/]+)\/copy$/)) && op.method === 'POST') {
      const from = roomById(m[1]!);
      const item = from?.items.find((i) => i.id === m![2]);
      const target = roomById(typeof body.toRoomId === 'string' ? body.toRoomId : m[1]!);
      if (!item || !target) continue;
      const count = Math.max(1, Number(body.count ?? 1));
      for (let n = 0; n < count; n++) {
        target.items.push({ ...item, id: `${opKey}:${n}`, measurementId: `${opKey}:${n}`, roomId: target.id, pendingSync: true } as OverlayItem);
      }
    } else if (path === 'address' && op.method === 'PATCH') {
      for (const field of ADDRESS_FIELDS) if (field in body) loose[field] = body[field] ?? null;
    } else if (path === 'location' && op.method === 'PATCH') {
      if (body.lat != null) loose.lat = String(body.lat);
      if (body.lng != null) loose.lng = String(body.lng);
    } else if (path === 'measure-notes' && op.method === 'PATCH') {
      Object.assign(loose, body);
    } else if (path === 'self-reschedule' && op.method === 'POST') {
      if (typeof body.measureAt === 'string') loose.measureAt = body.measureAt;
      if (typeof body.measurePeriod === 'string') loose.measurePeriod = body.measurePeriod;
    } else if (path === 'markup-discount' && op.method === 'PATCH') {
      for (const [key, value] of Object.entries(body)) loose[key] = typeof value === 'number' ? String(value) : value;
    } else if (path === 'complete-measure' && op.method === 'POST') {
      // Оформление (в т.ч. с подписью без сети) ждёт отправки: показываем заказ уже
      // оформленным — замерщик в производстве только смотрит, и подписанный состав
      // не разойдётся с заявкой до синхронизации.
      loose.status = 'PRODUCTION';
    } else if ((m = path.match(/^photos\/([^/]+)$/)) && op.method === 'DELETE') {
      order.photos = order.photos.filter((p) => p.id !== m![1]);
    }
  }

  // Итоги — по той же формуле, что recomputeTotals на сервере: subtotal = Σ изделий,
  // total = наценка → скидка → + легаси-аксессуары − кредит клиента. Аксессуаров и
  // кредита в карточке нет, поэтому их вклад берём как разницу из серверного total.
  const itemsSum = (o: OrderDetail) => o.rooms.flatMap((r) => r.items).reduce((sum, i) => sum + BigInt(i.priceTiyin || '0'), 0n);
  const sourceSubtotal = itemsSum(source);
  const extras = BigInt(source.totalTiyin || '0') - priceAfterMarkup(sourceSubtotal, source);
  const subtotal = itemsSum(order);
  order.subtotalTiyin = subtotal.toString();
  order.totalTiyin = (priceAfterMarkup(subtotal, order) + extras).toString();
  return order;
}

/** Сумма изделий после наценки и скидки заказа, до 100 сум (как recomputeTotals на сервере). */
function priceAfterMarkup(subtotal: bigint, o: OrderDetail): bigint {
  let total = subtotal;
  const markupTiyin = BigInt(o.markupTiyin || '0');
  const discountTiyin = BigInt(o.discountTiyin || '0');
  if (o.markupMode === 'SUM' && markupTiyin > 0n) total += markupTiyin;
  else if (Number(o.markupPct) > 0) total = applyMarkupAndDiscount(total, Number(o.markupPct), 0);
  if (o.discountMode === 'SUM' && discountTiyin > 0n) total = total > discountTiyin ? total - discountTiyin : 0n;
  else if (Number(o.discountPct) > 0) total = applyMarkupAndDiscount(total, 0, Number(o.discountPct));
  return roundOrderTotal(total);
}

/** Ожидающие (ещё не отправленные) записи этого заказа текущего сотрудника, по порядку. */
export async function pendingOpsForOrder(orderId: string): Promise<PendingOp[]> {
  const prefix = `/v1/orders/${orderId}/`;
  return db.outbox
    .where('status').anyOf('pending', 'in_flight')
    .filter((item) => belongsToCurrentUser(item) && item.url.startsWith(prefix))
    .sortBy('createdAt');
}

/** orderId из url outbox-записи (для точечной инвалидации кеша после отправки). */
export function orderIdOfUrl(url: string): string | null {
  return url.match(/^\/v1\/orders\/([^/?]+)\//)?.[1] ?? null;
}
